import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { invoiceAPI, clientAPI, accountAPI } from '../lib/api';
import { formatINR, apiError, todayISO as todayIso } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DatePicker from '../components/DatePicker';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import ClientPicker from '../components/ClientPicker';
import { useIsPhone } from '../hooks/use-breakpoint';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2,
  Download,
  Send,
} from 'lucide-react';

const DEFAULT_LINE = () => ({
  name: '',
  description: '',
  quantity: 1,
  rate: 0,
  discount_pct: '',
  hsn_sac: '',
});

// YYYY-MM-DD + n days, in local time (no UTC drift).
const addDays = (iso, n) => {
  const [y, m, d] = (iso || todayIso()).split('-').map(Number);
  const dt = new Date(y, m - 1, d + (Number(n) || 0));
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

// Whole days from ISO date a to ISO date b (UTC maths, so DST can't skew it).
const daysBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000) || 0;

// Half-up to paise on the decimal text of the number, like the server's Decimal quantize
// (so 1.005 -> 1.01, which Math.round(x * 100) gets wrong).
const round2 = (x) => {
  const n = Number(x) || 0;
  const a = Math.abs(n);
  const t = String(a);
  const r = t.includes('e') ? Math.round(a * 100) / 100 : Number(`${Math.round(Number(`${t}e2`))}e-2`);
  return n < 0 ? -r : r;
};

// What the server compares to decide whether the lines were touched (name, qty, rate, taxes, discount).
const lineKey = (ls) => JSON.stringify((ls || []).map((l) => [
  String(l.name || '').trim(), Number(l.quantity) || 0, Number(l.rate) || 0,
  Number(l.cgst_pct) || 0, Number(l.sgst_pct) || 0, Number(l.igst_pct) || 0, Number(l.discount_pct) || 0,
]));

export default function InvoiceForm() {
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    client_id: '',
    client_name: '',
    account_id: '',
    issued_at: todayIso(),
    due_at: todayIso(),
    terms: 'Due on Receipt',
    place_of_supply: 'Maharashtra (27)',
    subject: '',
    notes: 'Looking forward for your business.',
    status: 'draft',
    po_number: '',
    private_notes: '',
    bill_to_address: '',
    bill_to_city: '',
    bill_to_state: '',
    bill_to_country: 'India',
    bill_to_gstin: '',
  });

  const [lines, setLines] = useState([DEFAULT_LINE()]);
  const [termsTouched, setTermsTouched] = useState(false); // user typed custom terms → never auto-overwrite
  // GST handling
  const [interState, setInterState] = useState(false); // true → IGST 18%, false → CGST 9% + SGST 9%
  const [taxEnabled, setTaxEnabled] = useState(false); // DESENO is not GST-registered
  // Edit mode: until the GST switches are touched, lines keep their stored tax %, so untouched lines
  // go back byte-for-byte and the server keeps the stored money. `orig` = stored line key + money.
  const [taxTouched, setTaxTouched] = useState(false);
  const [orig, setOrig] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cRes, aRes] = await Promise.all([clientAPI.getAll(), accountAPI.getAll()]);
      setClients(cRes.data || []);
      setAccounts(aRes.data || []);

      if (isEditing) {
        const inv = (await invoiceAPI.getOne(id)).data;
        if (inv.status === 'cancelled') {
          toast.error('This invoice is cancelled. Reinstate it before editing.');
          navigate(`/invoices/${id}`, { replace: true });
          return;
        }
        setForm({
          client_id: inv.client_id || '',
          client_name: inv.client_name || '',
          account_id: inv.account_id || '',
          issued_at: inv.issued_at || todayIso(),
          due_at: inv.due_at || todayIso(),
          terms: inv.terms || 'Due on Receipt',
          place_of_supply: inv.place_of_supply || 'Maharashtra (27)',
          subject: inv.subject || '',
          notes: inv.notes || '',
          // paid/overdue are derived by the server; the form only sets draft/sent.
          status: inv.status === 'draft' ? 'draft' : 'sent',
          po_number: inv.po_number || '',
          private_notes: inv.private_notes || '',
          bill_to_address: inv.bill_to_address || '',
          bill_to_city: inv.bill_to_city || '',
          bill_to_state: inv.bill_to_state || '',
          bill_to_country: inv.bill_to_country || 'India',
          bill_to_gstin: inv.bill_to_gstin || '',
        });
        setLines(
          (inv.line_items && inv.line_items.length > 0)
            ? inv.line_items.map((l) => ({
                name: l.name || '',
                description: l.description || '',
                quantity: l.quantity ?? 1,
                rate: l.rate ?? 0,
                discount_pct: l.discount_pct ? l.discount_pct : '',
                hsn_sac: l.hsn_sac || '',
                _tax: { cgst: l.cgst_pct || 0, sgst: l.sgst_pct || 0, igst: l.igst_pct || 0 },
              }))
            : [DEFAULT_LINE()]
        );
        // Saved custom terms (not the auto "Net N"/"Due on Receipt") must survive a client change.
        setTermsTouched(!!inv.terms && !/^(Due on Receipt|Net \d+)$/.test(inv.terms));
        const gstOn = (inv.cgst + inv.sgst + inv.igst) > 0;
        setTaxEnabled(gstOn);
        setInterState((inv.igst || 0) > 0);
        setOrig({
          key: lineKey(inv.line_items),
          money: {
            subtotal: Number(inv.subtotal) || 0,
            discount: Number(inv.discount_amount) || 0,
            cgst: Number(inv.cgst) || 0,
            sgst: Number(inv.sgst) || 0,
            igst: Number(inv.igst) || 0,
            total: Number(inv.total) || 0,
            round_off: Number(inv.round_off) || 0,
          },
        });
      }
    } catch (err) {
      toast.error(apiError(err, 'Failed to load invoice data'));
      if (isEditing) navigate('/invoices');
    } finally {
      setLoading(false);
    }
  }, [id, isEditing, navigate]);

  useEffect(() => { load(); }, [load]);

  // When the client changes, snapshot their bill-to fields; due date follows their credit days.
  const onClientChange = (clientId) => {
    const c = clients.find((cl) => cl.id === clientId);
    if (!c) return;
    const days = Number(c.credit_days) || 0;
    setForm((f) => ({
      ...f,
      client_id: c.id,
      client_name: c.name,
      due_at: addDays(f.issued_at, days),
      ...(termsTouched ? {} : { terms: days > 0 ? `Net ${days}` : 'Due on Receipt' }),
      bill_to_address: c.bill_to_address || '',
      bill_to_city: c.bill_to_city || '',
      bill_to_state: c.bill_to_state || '',
      bill_to_country: c.bill_to_country || 'India',
      bill_to_gstin: c.gstin || '',
    }));
  };

  // Moving the issue date keeps the existing issued→due gap (even if the due date was edited by hand).
  const onIssuedChange = (issued) => {
    setForm((f) => ({
      ...f,
      issued_at: issued,
      ...(issued && f.issued_at && f.due_at ? { due_at: addDays(issued, daysBetween(f.issued_at, f.due_at)) } : {}),
    }));
  };

  // Tax + totals — recomputed on every change. Mirrors the server exactly:
  // amount = qty*rate; off = amount*disc%; tax on (amount-off); total = exact rounded half-up to a rupee.
  const totals = useMemo(() => {
    const toggled = {
      cgst: taxEnabled && !interState ? 9 : 0,
      sgst: taxEnabled && !interState ? 9 : 0,
      igst: taxEnabled && interState ? 18 : 0,
    };

    let subtotal = 0;
    let discount = 0;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;

    const lineOffs = [];
    const lineTotals = lines.map((l) => {
      const qty = parseFloat(l.quantity) || 0;
      const rate = parseFloat(l.rate) || 0;
      const discount_pct = parseFloat(l.discount_pct) || 0;
      const pct = (!taxTouched && l._tax) || toggled;
      const amount = round2(qty * rate);
      const off = round2(amount * discount_pct / 100);
      const net = amount - off;
      const cgst_amount = round2(net * pct.cgst / 100);
      const sgst_amount = round2(net * pct.sgst / 100);
      const igst_amount = round2(net * pct.igst / 100);
      subtotal += amount;
      discount += off;
      cgst += cgst_amount;
      sgst += sgst_amount;
      igst += igst_amount;
      lineOffs.push(off);
      return {
        name: l.name,
        description: l.description,
        hsn_sac: l.hsn_sac,
        quantity: qty,
        rate,
        discount_pct,
        amount,
        cgst_pct: pct.cgst,
        sgst_pct: pct.sgst,
        igst_pct: pct.igst,
        cgst_amount,
        sgst_amount,
        igst_amount,
      };
    });

    subtotal = round2(subtotal);
    discount = round2(discount);
    cgst = round2(cgst);
    sgst = round2(sgst);
    igst = round2(igst);
    const exact = subtotal - discount + cgst + sgst + igst;
    const total = Math.round(round2(exact));
    const calc = { subtotal, discount, cgst, sgst, igst, total, round_off: round2(total - exact) };
    // Lines untouched on an existing invoice: the server keeps the stored money, so show that.
    const fromStored = !!orig && orig.key === lineKey(lineTotals);
    return { ...(fromStored ? orig.money : calc), lineTotals, lineOffs, fromStored };
  }, [lines, taxEnabled, interState, taxTouched, orig]);

  const updateLine = (idx, field, value) => {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };
  const addLine = () => setLines((ls) => [...ls, DEFAULT_LINE()]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const handleSubmit = async (e, opts = {}) => {
    if (e) e.preventDefault();
    if (!form.client_id) return toast.error('Pick a client');
    if (totals.lineTotals.every((l) => l.amount === 0)) return toast.error('Add at least one line item with amount > 0');
    if (totals.lineTotals.some((l) => l.discount_pct < 0 || l.discount_pct > 100)) return toast.error('Discount % must be between 0 and 100');
    if (totals.total <= 0) return toast.error('Invoice total must be more than zero');
    setSaving(true);
    // Totals, payment_made/balance_due and the invoice number are server-owned.
    const payload = {
      ...form,
      line_items: totals.lineTotals,
      subtotal: totals.subtotal,
      cgst: totals.cgst,
      sgst: totals.sgst,
      igst: totals.igst,
      total: totals.total,
    };
    let saved;
    try {
      saved = isEditing
        ? (await invoiceAPI.update(id, payload)).data
        : (await invoiceAPI.create(payload)).data;
      toast.success(isEditing ? 'Invoice updated' : `Invoice ${saved.invoice_number} created`);
    } catch (err) {
      toast.error(apiError(err, 'Save failed'));
      setSaving(false);
      return;
    }
    // Saved: leave the form first so a failed PDF download can never re-submit (duplicate invoices).
    navigate(opts.sendAfter ? `/invoices/${saved.id}?action=send` : `/invoices/${saved.id}`);
    if (opts.downloadAfter) {
      try {
        const pdfRes = await invoiceAPI.downloadPdf(saved.id);
        const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice_${(saved.invoice_number || saved.id.slice(0, 8)).replace(/[^\w.-]+/g, '-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast.error('Invoice saved, but the PDF download failed. Use Download PDF on the invoice page.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Closed accounts are hidden (kept only if this invoice already names one).
  const pickableAccounts = accounts.filter((a) => a.status !== 'closed' || a.id === form.account_id);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/invoices">
            <Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope']">
              {isEditing ? 'Edit invoice' : 'New invoice'}
            </h1>
            {!isEditing && <p className="text-xs text-gray-500">The invoice number is assigned automatically when you save.</p>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={(e) => handleSubmit(e, { downloadAfter: true })} disabled={saving} className="flex-1 sm:flex-none whitespace-nowrap">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Save & download PDF
          </Button>
          <Button type="button" variant="outline" onClick={(e) => handleSubmit(e, { sendAfter: true })} disabled={saving} data-testid="save-and-send-btn" className="flex-1 sm:flex-none whitespace-nowrap">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Save & send
          </Button>
          <Button type="button" onClick={(e) => handleSubmit(e)} disabled={saving} className="bg-black hover:bg-black/90 flex-1 sm:flex-none whitespace-nowrap">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
        </div>
      </div>

      <form onSubmit={(e) => handleSubmit(e)} className="space-y-6">
        {/* Top fields */}
        <Card>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Client *</Label>
              <ClientPicker clients={clients} value={form.client_id} onChange={onClientChange} placeholder={form.client_name || 'Pick a client'} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-status">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger id="inv-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-issued">Issued *</Label>
              <DatePicker id="inv-issued" value={form.issued_at} onChange={onIssuedChange} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-due">Due *</Label>
              <DatePicker id="inv-due" value={form.due_at} onChange={(v) => setForm({ ...form, due_at: v })} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-terms">Terms</Label>
              <Input id="inv-terms" value={form.terms} onChange={(e) => { setTermsTouched(true); setForm({ ...form, terms: e.target.value }); }} />
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="inv-subject">Subject</Label>
              <Input id="inv-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Month - May" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-account">Receiving account</Label>
              <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                <SelectTrigger id="inv-account"><SelectValue placeholder="Pick an account" /></SelectTrigger>
                <SelectContent>
                  {pickableAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}{a.status === 'closed' ? ' (closed)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-po">PO number</Label>
              <Input id="inv-po" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} placeholder="Client's PO / reference" data-testid="invoice-po-input" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="inv-private-note">Internal note <span className="text-xs font-normal text-gray-500">— not printed</span></Label>
              <Textarea
                id="inv-private-note"
                rows={2}
                value={form.private_notes}
                onChange={(e) => setForm({ ...form, private_notes: e.target.value })}
                placeholder="Only visible inside PriceIntel"
                data-testid="invoice-private-notes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* GST controls */}
        <Card>
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex items-center gap-3">
              <Switch id="inv-gst" checked={taxEnabled} onCheckedChange={(v) => { setTaxTouched(true); setTaxEnabled(v); }} />
              <div>
                <Label htmlFor="inv-gst" className="text-sm">Tax Invoice (GST)</Label>
                <p className="text-xs text-gray-500">
                  DESENO is not GST-registered — leave OFF unless needed
                </p>
              </div>
            </div>
            {taxEnabled && (
              <div className="flex items-center gap-3">
                <Switch id="inv-igst" checked={interState} onCheckedChange={(v) => { setTaxTouched(true); setInterState(v); }} />
                <div>
                  <Label htmlFor="inv-igst" className="text-sm">Inter-state (IGST 18%)</Label>
                  <p className="text-xs text-gray-500">
                    Off = same state (27 MH) → CGST 9% + SGST 9%
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-1.5 w-full sm:w-auto sm:ml-auto">
              <Label htmlFor="inv-pos">Place of supply</Label>
              <Input
                id="inv-pos"
                value={form.place_of_supply}
                onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })}
                placeholder="Maharashtra (27)"
              />
            </div>
          </CardContent>
        </Card>

        {/* Line items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Line items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addLine}>
              <Plus className="w-4 h-4 mr-1" /> Add row
            </Button>
          </CardHeader>
          <CardContent className="p-0 overflow-auto">
            {isPhone ? (
              <div className="divide-y" data-testid="line-items-phone">
                {totals.lineTotals.map((l, i) => (
                  <div key={i} className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Item {i + 1}</span>
                      {lines.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-700" onClick={() => removeLine(i)} aria-label={`Remove item ${i + 1}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <Input value={lines[i].name} onChange={(e) => updateLine(i, 'name', e.target.value)} placeholder="Item, e.g. Social Media Marketing" aria-label={`Item ${i + 1} name`} />
                    <Input value={lines[i].description} onChange={(e) => updateLine(i, 'description', e.target.value)} placeholder="Description / period" aria-label={`Item ${i + 1} description`} />
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor={`pl-qty-${i}`} className="text-xs text-gray-500">Qty</Label>
                        <Input id={`pl-qty-${i}`} type="number" inputMode="decimal" value={lines[i].quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)} className="text-right font-mono" step="0.01" />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`pl-rate-${i}`} className="text-xs text-gray-500">Rate</Label>
                        <Input id={`pl-rate-${i}`} type="number" inputMode="decimal" value={lines[i].rate} onChange={(e) => updateLine(i, 'rate', e.target.value)} className="text-right font-mono" step="0.01" />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`pl-disc-${i}`} className="text-xs text-gray-500">Disc %</Label>
                        <Input id={`pl-disc-${i}`} type="number" inputMode="decimal" value={lines[i].discount_pct} onChange={(e) => updateLine(i, 'discount_pct', e.target.value)} className="text-right font-mono" step="0.01" min="0" max="100" placeholder="0" data-testid={`line-discount-phone-${i}`} />
                      </div>
                    </div>
                    {taxEnabled && (
                      <p className="text-xs text-gray-500 font-mono text-right">
                        {interState ? `IGST ${formatINR(l.igst_amount)}` : `CGST ${formatINR(l.cgst_amount)} · SGST ${formatINR(l.sgst_amount)}`}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Amount</span>
                      <span className="font-mono font-medium">
                        {formatINR(l.amount)}
                        {totals.lineOffs[i] > 0 && <span className="ml-2 text-xs text-gray-500">−{formatINR(totals.lineOffs[i])}</span>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left">
                  <th className="py-2 px-3 font-semibold w-[40px]">#</th>
                  <th className="py-2 px-3 font-semibold">Item</th>
                  <th className="py-2 px-3 font-semibold w-[80px] text-right">Qty</th>
                  <th className="py-2 px-3 font-semibold w-[120px] text-right">Rate</th>
                  <th className="py-2 px-3 font-semibold w-[90px] text-right">Disc %</th>
                  {taxEnabled && !interState && <>
                    <th className="py-2 px-3 font-semibold w-[110px] text-right">CGST 9%</th>
                    <th className="py-2 px-3 font-semibold w-[110px] text-right">SGST 9%</th>
                  </>}
                  {taxEnabled && interState && (
                    <th className="py-2 px-3 font-semibold w-[110px] text-right">IGST 18%</th>
                  )}
                  <th className="py-2 px-3 font-semibold w-[120px] text-right">Amount</th>
                  <th className="py-2 px-3 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {totals.lineTotals.map((l, i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="py-2 px-3 text-gray-500 text-xs">{i + 1}</td>
                    <td className="py-2 px-3">
                      <Input
                        value={lines[i].name}
                        onChange={(e) => updateLine(i, 'name', e.target.value)}
                        aria-label={`Item ${i + 1} name`}
                        className="h-8 text-sm font-medium"
                        placeholder="e.g. Social Media Marketing"
                      />
                      <Input
                        value={lines[i].description}
                        onChange={(e) => updateLine(i, 'description', e.target.value)}
                        aria-label={`Item ${i + 1} description`}
                        className="h-7 text-xs mt-1 text-gray-500"
                        placeholder="Description / period"
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Input
                        type="number"
                        value={lines[i].quantity}
                        onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                        aria-label={`Item ${i + 1} quantity`}
                        className="h-8 text-sm text-right font-mono"
                        step="0.01"
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Input
                        type="number"
                        value={lines[i].rate}
                        onChange={(e) => updateLine(i, 'rate', e.target.value)}
                        aria-label={`Item ${i + 1} rate`}
                        className="h-8 text-sm text-right font-mono"
                        step="0.01"
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Input
                        type="number"
                        value={lines[i].discount_pct}
                        onChange={(e) => updateLine(i, 'discount_pct', e.target.value)}
                        aria-label={`Item ${i + 1} discount percent`}
                        className="h-8 text-sm text-right font-mono"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="0"
                        data-testid={`line-discount-${i}`}
                      />
                    </td>
                    {taxEnabled && !interState && <>
                      <td className="py-2 px-3 text-right font-mono text-xs text-gray-600">{formatINR(l.cgst_amount)}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs text-gray-600">{formatINR(l.sgst_amount)}</td>
                    </>}
                    {taxEnabled && interState && (
                      <td className="py-2 px-3 text-right font-mono text-xs text-gray-600">{formatINR(l.igst_amount)}</td>
                    )}
                    <td className="py-2 px-3 text-right font-mono">
                      {formatINR(l.amount)}
                      {totals.lineOffs[i] > 0 && <p className="text-xs text-gray-500">−{formatINR(totals.lineOffs[i])}</p>}
                    </td>
                    <td className="py-2 px-3">
                      {lines.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-700" onClick={() => removeLine(i)} aria-label={`Remove item ${i + 1}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </CardContent>
        </Card>

        {/* Bill-to + Totals side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Bill-to (snapshot at issue)</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="bt-address">Address</Label>
                <Textarea
                  id="bt-address"
                  rows={2}
                  value={form.bill_to_address}
                  onChange={(e) => setForm({ ...form, bill_to_address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 min-[420px]:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="bt-city">City</Label>
                  <Input id="bt-city" value={form.bill_to_city} onChange={(e) => setForm({ ...form, bill_to_city: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bt-state">State</Label>
                  <Input id="bt-state" value={form.bill_to_state} onChange={(e) => setForm({ ...form, bill_to_state: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bt-country">Country</Label>
                  <Input id="bt-country" value={form.bill_to_country} onChange={(e) => setForm({ ...form, bill_to_country: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bt-gstin">Client GSTIN</Label>
                <Input id="bt-gstin" value={form.bill_to_gstin} onChange={(e) => setForm({ ...form, bill_to_gstin: e.target.value.toUpperCase() })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bt-notes">Notes</Label>
                <Textarea
                  id="bt-notes"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Totals</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-2">
              <Row label="Sub Total" value={formatINR(totals.subtotal)} />
              {totals.discount > 0 && <Row label="Discount" value={`−${formatINR(totals.discount)}`} />}
              {taxEnabled && (
                <>
                  <Row label="Taxable amount" value={formatINR(round2(totals.subtotal - totals.discount))} />
                  {!interState && <>
                    <Row label="CGST 9%" value={formatINR(totals.cgst)} />
                    <Row label="SGST 9%" value={formatINR(totals.sgst)} />
                  </>}
                  {interState && <Row label="IGST 18%" value={formatINR(totals.igst)} />}
                </>
              )}
              {Math.abs(totals.round_off) > 0.001 && (
                <Row label="Round off" value={`${totals.round_off > 0 ? '+' : ''}${formatINR(totals.round_off)}`} />
              )}
              <div className="flex justify-between items-center bg-black text-white rounded-md px-3 py-2 mt-3" data-testid="invoice-form-total">
                <span className="font-semibold">Total</span>
                <span className="font-mono font-bold text-lg">{formatINR(totals.total)}</span>
              </div>
              {totals.fromStored && (
                <p className="text-xs text-gray-500 pt-2">Line items unchanged: the saved amounts are kept as they are.</p>
              )}
              <p className="text-xs text-gray-500 pt-2">
                Payments are recorded from the invoice page (Record payment).
              </p>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
